import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Maximize2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useTenantApiToken } from '../../contexts/TenantProfileContext';
import {
  getPortalContentPlayback,
  recordPortalContentEvent,
  type PortalContentEngagementRollup,
  type PortalContentPlayback,
} from '../../services/tenantApi';
import TenantPortalVideoLoadingSkeleton from './TenantPortalVideoLoadingSkeleton';
import TenantPortalVideoPlaceholder from './TenantPortalVideoPlaceholder';

interface TenantPortalVideoSlotProps {
  pageSlug: string;
  sectionName: string;
  /** When false, always show {@link TenantPortalVideoPlaceholder} (no API fetch). */
  fetchPlayback?: boolean;
  placeholderLabel: string;
  placeholderSublabel?: string;
  /** Optional heading above the video area (card layout). */
  title?: string;
  description?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  /** Aspect utility for the player. Defaults to 16:9. */
  playerAspectClass?: string;
  /**
   * Fill a sibling-sized card and center the 16:9 player in the leftover space.
   * The match applies from the `lg` breakpoint, where the card is given a height.
   */
  centerInCard?: boolean;
  className?: string;
  bare?: boolean;
  /** Discovery Call step: complete after cumulative watch time reaches half the video. */
  minuteStep?: boolean;
  onEngagement?: (engagement: PortalContentEngagementRollup | null) => void;
}

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;

function computeWatchPercent(currentTime: number, duration: number): number {
  if (duration <= 0) return 0;
  return Math.min(100, Math.max(0, Math.floor((currentTime / duration) * 100)));
}

function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
}

/**
 * CMS-backed video slot: placeholder until upload, then presigned playback + watch analytics.
 * Mirrors move-out AutoPlayVideoPlayer / InlineVideo behavior without CloudFront URLs.
 */
export default function TenantPortalVideoSlot({
  pageSlug,
  sectionName,
  fetchPlayback = true,
  placeholderLabel,
  placeholderSublabel,
  title,
  description,
  titleClassName = 'text-xl font-bold text-gray-900 dark:text-white sm:text-2xl',
  descriptionClassName = 'mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base',
  headerClassName = 'border-b border-gray-100 dark:border-white/5 px-4 py-4 sm:px-8 sm:py-6',
  bodyClassName = 'px-4 py-4 sm:px-8 sm:py-6',
  playerAspectClass = 'aspect-video',
  centerInCard = false,
  className = '',
  bare = false,
  minuteStep = false,
  onEngagement,
}: TenantPortalVideoSlotProps) {
  const getToken = useTenantApiToken();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const playbackRef = useRef<PortalContentPlayback | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastReportedPercentRef = useRef(0);
  const lastPlaybackTimeRef = useRef<number | null>(null);
  const pendingWatchSecondsRef = useRef(0);
  const serverWatchSecondsRef = useRef(0);
  const watchedHalfRef = useRef(false);
  const flushInFlightRef = useRef(false);
  const progressTimerRef = useRef<number | null>(null);
  const onEngagementRef = useRef(onEngagement);
  onEngagementRef.current = onEngagement;

  const scrubberRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const [loading, setLoading] = useState(fetchPlayback);
  const [playback, setPlayback] = useState<PortalContentPlayback | null>(null);
  const [engagement, setEngagement] = useState<PortalContentEngagementRollup | null>(null);
  /** CMS content that has shown a decoded frame — stable across presigned URL rotation. */
  const [readyContentId, setReadyContentId] = useState<string | null>(null);
  const hasPresentedFirstFrameRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadPlayback = useCallback(async () => {
    if (!fetchPlayback) {
      setLoading(false);
      return;
    }
    const hadStream = Boolean(playbackRef.current?.playbackUrl);
    if (!hadStream) setLoading(true);
    try {
      const data = await getPortalContentPlayback(getTokenRef.current, pageSlug, sectionName);
      setPlayback((prev) => {
        if (prev && data.contentId === prev.contentId && prev.playbackUrl) {
          return { ...data, playbackUrl: prev.playbackUrl };
        }
        return data;
      });
      setEngagement(data.engagement);
      lastReportedPercentRef.current = data.engagement.maxWatchPercent;
    } catch {
      if (!hadStream) {
        setPlayback(null);
        setEngagement(null);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchPlayback, pageSlug, sectionName]);

  useEffect(() => {
    playbackRef.current = playback;
  }, [playback]);

  useEffect(() => {
    void loadPlayback();
  }, [loadPlayback]);

  useEffect(() => {
    if (loading) return;
    serverWatchSecondsRef.current = engagement?.totalWatchSeconds ?? 0;
    watchedHalfRef.current = engagement?.watchedHalf === true;
    onEngagementRef.current?.(engagement);
  }, [engagement, loading]);

  const sendEvent = useCallback(
    async (payload: {
      eventType: 'PLAY' | 'PAUSE' | 'PROGRESS';
      watchPercent?: number;
      watchSeconds?: number;
      watchSecondsDelta?: number;
      videoDurationSeconds?: number;
    }): Promise<boolean> => {
      try {
        const result = await recordPortalContentEvent(getToken, pageSlug, sectionName, payload);
        setEngagement(result.engagement);
        serverWatchSecondsRef.current = result.engagement.totalWatchSeconds;
        watchedHalfRef.current = result.engagement.watchedHalf;
        if (payload.eventType === 'PROGRESS' && payload.watchPercent != null) {
          lastReportedPercentRef.current = Math.max(
            lastReportedPercentRef.current,
            payload.watchPercent,
          );
        }
        return true;
      } catch {
        return false;
      }
    },
    [getToken, pageSlug, sectionName],
  );

  const flushCumulativeWatch = useCallback(async () => {
    if (!minuteStep || flushInFlightRef.current) return;
    const video = videoRef.current;
    const pending = pendingWatchSecondsRef.current;
    if (!video || pending < 1 || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const delta = Math.min(20, Math.round(pending));
    if (delta < 1) return;
    pendingWatchSecondsRef.current -= delta;
    flushInFlightRef.current = true;
    const recorded = await sendEvent({
      eventType: 'PROGRESS',
      watchSecondsDelta: delta,
      videoDurationSeconds: Math.round(video.duration),
    });
    flushInFlightRef.current = false;
    if (!recorded) pendingWatchSecondsRef.current += delta;
    else if (pendingWatchSecondsRef.current >= 1) void flushCumulativeWatch();
  }, [minuteStep, sendEvent]);

  const reportProgressFromVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;

    if (minuteStep && !video.paused) {
      const now = video.currentTime;
      const last = lastPlaybackTimeRef.current;
      lastPlaybackTimeRef.current = now;
      if (last != null) {
        const delta = now - last;
        if (delta > 0 && delta <= 3) pendingWatchSecondsRef.current += delta;
      }
      const pending = pendingWatchSecondsRef.current;
      const crossesHalf =
        !watchedHalfRef.current && serverWatchSecondsRef.current + pending >= video.duration * 0.5;
      if (pending >= 5 || crossesHalf) void flushCumulativeWatch();
    }

    const watchPercent = computeWatchPercent(video.currentTime, video.duration);
    const last = lastReportedPercentRef.current;
    if (watchPercent <= last) return;

    const milestones = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100].filter(
      (m) => m <= watchPercent && m > last,
    );
    const highest = milestones[milestones.length - 1];
    if (highest == null) return;

    void sendEvent({
      eventType: 'PROGRESS',
      watchPercent: highest,
      watchSeconds: Math.floor(video.currentTime),
      videoDurationSeconds: Math.floor(video.duration),
    });
  }, [flushCumulativeWatch, minuteStep, sendEvent]);

  useEffect(() => {
    const flush = () => {
      void flushCumulativeWatch();
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [flushCumulativeWatch]);

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (video && !scrubbing) setCurrentTime(video.currentTime);
    if (progressTimerRef.current != null) return;
    progressTimerRef.current = window.setTimeout(() => {
      progressTimerRef.current = null;
      reportProgressFromVideo();
    }, 400);
  };

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      const video = videoRef.current;
      if (!video || video.paused) return;
      setControlsVisible(false);
      setSpeedOpen(false);
    }, 500);
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    const video = videoRef.current;
    if (video && !video.paused && !scrubbing) scheduleHide();
  }, [scheduleHide, scrubbing]);

  const notePointer = useCallback((clientX: number, clientY: number) => {
    const last = lastPointerRef.current;
    if (last && last.x === clientX && last.y === clientY) return;
    lastPointerRef.current = { x: clientX, y: clientY };
    showControls();
  }, [showControls]);

  const hideControls = useCallback(() => {
    if (scrubbing) return;
    if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    setControlsVisible(false);
    setSpeedOpen(false);
  }, [scrubbing]);

  useEffect(() => {
    const syncFullscreen = () => {
      const active = document.fullscreenElement === frameRef.current;
      setFullscreen(active);
      if (active) setExpanded(false);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  const covering = fullscreen || expanded;

  const toggleFullscreen = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement === frame || expanded) {
      if (document.fullscreenElement) void document.exitFullscreen();
      setExpanded(false);
      return;
    }
    const request = frame.requestFullscreen?.();
    if (!request) {
      setExpanded(true);
      return;
    }
    const timer = window.setTimeout(() => {
      if (document.fullscreenElement !== frame) setExpanded(true);
    }, 250);
    void request
      .then(() => {
        window.clearTimeout(timer);
        if (document.fullscreenElement !== frame) setExpanded(true);
      })
      .catch(() => {
        window.clearTimeout(timer);
        setExpanded(true);
      });
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  useEffect(() => {
    if (!playing) {
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      setControlsVisible(true);
      return;
    }
    scheduleHide();
    return () => {
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    };
  }, [playing, scheduleHide]);

  useEffect(() => {
    if (!covering || !playing) return;
    const onMove = (event: PointerEvent) => notePointer(event.clientX, event.clientY);
    document.addEventListener('pointermove', onMove);
    return () => document.removeEventListener('pointermove', onMove);
  }, [covering, notePointer, playing]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const restart = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    void video.play();
  }, []);

  const changeSpeed = useCallback((rate: number) => {
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
    setSpeed(rate);
  }, []);

  const skipBy = useCallback((delta: number) => {
    const video = videoRef.current;
    if (!video) return;
    const length = Number.isFinite(video.duration) ? video.duration : 0;
    video.currentTime = Math.max(0, Math.min(length || video.currentTime + delta, video.currentTime + delta));
  }, []);

  const seekFromClientX = useCallback((clientX: number) => {
    const video = videoRef.current;
    const bar = scrubberRef.current;
    if (!video || !bar || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const nextTime = percent * video.duration;
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, []);

  const onScrubStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic events cannot capture a pointer.
    }
    setScrubbing(true);
    setControlsVisible(true);
    seekFromClientX(event.clientX);
  }, [seekFromClientX]);

  const onScrubMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbing) return;
    seekFromClientX(event.clientX);
  }, [scrubbing, seekFromClientX]);

  const onScrubEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbing) return;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may already have been released.
    }
    setScrubbing(false);
    const video = videoRef.current;
    if (video && !video.paused) scheduleHide();
  }, [scheduleHide, scrubbing]);

  useEffect(
    () => () => {
      if (progressTimerRef.current != null) window.clearTimeout(progressTimerRef.current);
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    },
    [],
  );

  const playbackUrl = fetchPlayback ? (playback?.playbackUrl ?? null) : null;
  const contentId = playback?.contentId ?? null;
  const showPlaceholder = !fetchPlayback || (!loading && !playbackUrl);
  const initialVideoReady =
    Boolean(playbackUrl) && contentId !== null && readyContentId === contentId;
  /** Black load screen until first decoded frame; ignore background API refetches. */
  const showInitialLoadScreen =
    (loading && !playbackUrl) || (Boolean(playbackUrl) && !initialVideoReady);
  const playerChromeVisible = Boolean(playbackUrl) && initialVideoReady;

  const tryMarkInitialVideoReady = useCallback(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl || !contentId) return;
    if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
    hasPresentedFirstFrameRef.current = true;
    setReadyContentId(contentId);
  }, [contentId, playbackUrl]);

  useEffect(() => {
    if (!contentId) {
      hasPresentedFirstFrameRef.current = false;
      setReadyContentId(null);
      return;
    }
    setReadyContentId((prev) => (prev !== null && prev !== contentId ? null : prev));
    const video = videoRef.current;
    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      tryMarkInitialVideoReady();
    }
  }, [contentId, tryMarkInitialVideoReady]);

  const configuredStreamRef = useRef<string | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return;
    if (configuredStreamRef.current === playbackUrl) return;
    configuredStreamRef.current = playbackUrl;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playbackRate = 1;
    setMuted(true);
    setSpeed(1);
    setPlaying(false);
  }, [playbackUrl]);

  const playerShellClass =
    `relative w-full max-w-full shrink-0 overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 bg-black ${playerAspectClass}`.trim();

  const videoEventHandlers = {
    onLoadedMetadata: () => {
      const video = videoRef.current;
      if (video && Number.isFinite(video.duration)) setDuration(video.duration);
      tryMarkInitialVideoReady();
    },
    onLoadedData: () => tryMarkInitialVideoReady(),
    onPlaying: () => {
      tryMarkInitialVideoReady();
      setPlaying(true);
    },
  };

  const videoBlock = showPlaceholder ? (
    <TenantPortalVideoPlaceholder
      label={placeholderLabel}
      sublabel={placeholderSublabel}
      aspectClass={playerAspectClass}
    />
  ) : (
    <div className={playerShellClass}>
      {showInitialLoadScreen && <TenantPortalVideoLoadingSkeleton overlay />}
      {playbackUrl ? (
      <div
        ref={frameRef}
        className={`group select-none bg-black ${
          expanded ? 'fixed inset-0 z-[80] h-screen w-screen' : 'absolute inset-0'
        }`}
        {...(playerChromeVisible
          ? {
              onMouseMove: (event: MouseEvent) => notePointer(event.clientX, event.clientY),
              onMouseLeave: () => {
                if (playing) hideControls();
              },
              onClick: () => {
                if (controlsVisible && playing) {
                  setControlsVisible(false);
                  setSpeedOpen(false);
                  return;
                }
                showControls();
              },
            }
          : {})}
      >
        <video
          ref={videoRef}
          className={`pointer-events-none absolute inset-0 h-full w-full bg-black object-contain ${
            initialVideoReady ? 'opacity-100' : 'opacity-0'
          }`}
          controls={false}
          loop
          muted={muted}
          playsInline
          preload="auto"
          src={playbackUrl ?? undefined}
          {...videoEventHandlers}
          onPlay={() => {
            setPlaying(true);
            const video = videoRef.current;
            void sendEvent({
              eventType: 'PLAY',
              watchSeconds: video ? Math.floor(video.currentTime) : undefined,
              videoDurationSeconds:
                video && Number.isFinite(video.duration) ? Math.floor(video.duration) : undefined,
            });
          }}
          onPause={() => {
            setPlaying(false);
            const video = videoRef.current;
            if (video && lastPlaybackTimeRef.current != null) {
              const delta = video.currentTime - lastPlaybackTimeRef.current;
              if (delta > 0 && delta <= 3) pendingWatchSecondsRef.current += delta;
            }
            if (video) lastPlaybackTimeRef.current = video.currentTime;
            void flushCumulativeWatch();
            void sendEvent({
              eventType: 'PAUSE',
              watchSeconds: video ? Math.floor(video.currentTime) : undefined,
              videoDurationSeconds:
                video && Number.isFinite(video.duration) ? Math.floor(video.duration) : undefined,
            });
          }}
          onSeeked={() => {
            const video = videoRef.current;
            if (video) lastPlaybackTimeRef.current = video.currentTime;
          }}
          onEnded={() => {
            const video = videoRef.current;
            if (video?.loop && !video.paused) return;
            setPlaying(false);
            if (video && lastPlaybackTimeRef.current != null) {
              const delta = video.currentTime - lastPlaybackTimeRef.current;
              if (delta > 0 && delta <= 3) pendingWatchSecondsRef.current += delta;
            }
            if (video) lastPlaybackTimeRef.current = video.currentTime;
            void flushCumulativeWatch();
          }}
          onVolumeChange={() => {
            const video = videoRef.current;
            if (video) setMuted(video.muted);
          }}
          onTimeUpdate={onTimeUpdate}
        />
        {playerChromeVisible && (
          <>
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent ${
            controlsVisible ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/70 to-transparent ${
            controlsVisible ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`absolute right-3 top-3 z-20 flex flex-col items-end gap-2 ${
            controlsVisible
              ? 'opacity-100'
              : 'pointer-events-none opacity-0'
          }`}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              restart();
              showControls();
            }}
            className="flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-900 dark:text-white shadow-lg transition-all hover:bg-gray-100"
          >
            <RefreshCw size={14} />
            <span>Restart video</span>
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleFullscreen();
              showControls();
            }}
            className="flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-900 dark:text-white shadow-lg transition-all hover:bg-gray-100"
            aria-label={covering ? 'Close full screen' : 'Full screen'}
          >
            {covering ? <X size={14} /> : <Maximize2 size={14} />}
            <span>{covering ? 'Close' : 'Full screen'}</span>
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setSpeedOpen((open) => !open);
                showControls();
              }}
              className="flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-900 dark:text-white shadow-lg transition-all hover:bg-gray-100"
            >
              <span>{speed === 1 ? '1x' : `${speed}x`} Speed</span>
              <ChevronDown size={14} className={`transition-transform ${speedOpen ? 'rotate-180' : ''}`} />
            </button>
            {speedOpen && (
              <div
                className="absolute right-0 top-full z-10 mt-1 min-w-[112px] overflow-hidden rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-xl"
                onClick={(event) => event.stopPropagation()}
              >
                {PLAYBACK_SPEEDS.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      changeSpeed(rate);
                      setSpeedOpen(false);
                      showControls();
                    }}
                    className={`w-full px-4 py-2 text-left text-xs font-medium transition-colors ${
                      speed === rate ? 'bg-red-600 text-white' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {rate === 1 ? '1x (Normal)' : `${rate}x`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center ${
          controlsVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="pointer-events-none relative flex items-center gap-3 sm:gap-6">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                skipBy(-10);
              }}
              className={`relative flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white shadow-xl backdrop-blur-sm transition hover:bg-black/75 active:scale-95 sm:h-14 sm:w-14 ${
                controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'
              }`}
              aria-label="Skip back 10 seconds"
            >
              <RotateCcw className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={2.25} />
              <span className="absolute mt-1.5 text-[9px] font-bold leading-none">10</span>
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                togglePlay();
              }}
              className={`flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-2xl ring-4 ring-white/20 transition hover:bg-red-500 active:scale-95 sm:h-20 sm:w-20 ${
                controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'
              }`}
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? (
                <Pause className="h-7 w-7 sm:h-10 sm:w-10" fill="currentColor" strokeWidth={0} />
              ) : (
                <Play className="ml-0.5 h-7 w-7 sm:ml-1 sm:h-10 sm:w-10" fill="currentColor" strokeWidth={0} />
              )}
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                skipBy(10);
              }}
              className={`relative flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white shadow-xl backdrop-blur-sm transition hover:bg-black/75 active:scale-95 sm:h-14 sm:w-14 ${
                controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'
              }`}
              aria-label="Skip forward 10 seconds"
            >
              <RotateCw className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={2.25} />
              <span className="absolute mt-1.5 text-[9px] font-bold leading-none">10</span>
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            toggleSound();
          }}
          className={`absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 translate-y-[calc(1.75rem+0.75rem)] items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium shadow-lg sm:translate-y-[calc(2.5rem+0.75rem)] ${
            controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
          } ${
            muted
              ? 'bg-white text-gray-900 hover:bg-gray-100'
              : 'bg-black/60 text-white hover:bg-black/80'
          }`}
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          <span>{muted ? 'Play with sound' : 'Turn sound off'}</span>
        </button>
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 px-3 pb-2.5 pt-6 ${
            controlsVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium tabular-nums text-white drop-shadow">
            <span>{formatClock(currentTime)}</span>
            <span>{formatClock(duration)}</span>
          </div>
          <div
            ref={scrubberRef}
            role="slider"
            aria-label="Video progress"
            aria-valuemin={0}
            aria-valuemax={Math.floor(duration)}
            aria-valuenow={Math.floor(currentTime)}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={onScrubStart}
            onPointerMove={onScrubMove}
            onPointerUp={onScrubEnd}
            onPointerCancel={onScrubEnd}
            className={`group/scrub relative h-2 cursor-pointer touch-none rounded-full bg-white/25 ${
              controlsVisible
                ? 'pointer-events-auto'
                : 'pointer-events-none'
            }`}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-red-600"
              style={{
                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                transition: scrubbing ? 'none' : 'width 0.15s linear',
              }}
            />
            <div
              className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-600 shadow-lg ring-2 ring-white transition-transform ${
                scrubbing ? 'scale-125' : 'group-hover/scrub:scale-110'
              }`}
              style={{
                left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                transition: scrubbing ? 'none' : 'left 0.15s linear, transform 0.15s ease',
              }}
            />
          </div>
        </div>
          </>
        )}
      </div>
      ) : null}
    </div>
  );

  if (bare) {
    return <div className={className}>{videoBlock}</div>;
  }

  const watched = engagement?.watched ?? playback?.engagement.watched;
  const minuteComplete = engagement?.watchedHalf === true;

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-bento dark:border-white/10 dark:bg-gray-800 ${centerInCard ? 'lg:flex lg:h-full lg:min-h-0 lg:flex-col' : ''} ${className}`.trim()}>
      {minuteStep && minuteComplete && (
        <span
          className="absolute right-4 top-4 z-10 grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 sm:right-5 sm:top-5"
          aria-label="Complete"
        >
          <Check size={16} strokeWidth={2.5} aria-hidden="true" />
        </span>
      )}
      {(title || description) && (
        <div className={`${centerInCard ? 'lg:shrink-0 ' : ''}${headerClassName}`}>
          <div className="flex items-start justify-between gap-3">
            <div className={minuteStep ? 'pr-8' : undefined}>
              {title && <h2 className={titleClassName}>{title}</h2>}
              {description && (
                <p className={descriptionClassName}>{description}</p>
              )}
              {playback?.caption && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{playback.caption}</p>
              )}
            </div>
            {!minuteStep && watched && (
              <p className="flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-700 sm:text-sm">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Watched
              </p>
            )}
          </div>
        </div>
      )}
      <div className={`${bodyClassName}${centerInCard ? ' lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:justify-center' : ''}`}>
        <div className="w-full">{videoBlock}</div>
      </div>
    </div>
  );
}
